import React, { useState, useEffect, useContext } from 'react'
import _ from 'lodash'
import AutoResizableValue from './value/auto-resizable-value'
import i18n from 'live/services/i18n'
import { WidgetMessage, SimpleWidgetMessage } from 'plugin-assets/shared/widget/editor/preview/widget-empty-state'
import { memoizedApi } from '../../lib/api'
import Utils from 'live/lib/utils'
import { WidgetContext } from 'live/context/widget'

type ChannelsValueEvent = Event & {
    value?: string
    formatted_value?: string
    uom?: string
}

type ChannelsWidgetProps = LiveReactWidgetProps & {
    event?: ChannelsValueEvent
}

export default function ChannelsValue(props: ChannelsWidgetProps): JSX.Element {
    const { __uom = {} } = props.meta || {}
    const { widget } = useContext(WidgetContext)
    const config = widget.jsonConfig
    const hasValue = _.size(props.event) > 1
    const [channel, setChannel] = useState(getChannel(config) || {})
    const asset = _.get(config, 'assets[0]', null)
    const disableFormat = _.get(config, 'disableFormat', false)
    const minimumFractionDigits = _.get(config, 'minimumFractionDigits', 0)
    const maximumFractionDigits = _.get(config, 'maximumFractionDigits', 0)
    const unformattedValue = props.event && props.event?.[channel?.qualifier]
    const value = disableFormat
        ? unformattedValue
        : Utils.formatValue(unformattedValue, minimumFractionDigits, maximumFractionDigits)
    const isChangingChannel = hasValue && value == null
    const containerPadding = 12

    useEffect(() => {
        memoizedApi.getAssets().then(visibleAssets => {
            if (!asset.lookup && !_.find(visibleAssets, { id: asset?.assetId })) setChannel(null)
        })
    }, [asset])

    useEffect(() => {
        const newChannel = getChannel(config)

        if (newChannel.qualifier !== channel.qualifier) setChannel(newChannel)
    }, [channel.qualifier, config])

    if (isChangingChannel) return <WidgetMessage message={i18n('Loading...')} />

    if (!channel) return <WidgetMessage message={i18n('No channel selected')} />

    if (value == null) return <SimpleWidgetMessage message={i18n('No data')} />

    return (
        <AutoResizableValue
            {...config}
            containerWidth={props.width - containerPadding}
            containerHeight={props.height}
            value={value}
            uom={__uom[channel.qualifier]}
        />
    )
}

function getChannel(config) {
    return _.get(config, 'assets[0].channels[0]', _.get(config, 'assets[0].calculatedChannels[0]', null))
}
